import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  IconSend,
  IconInfoCircle,
  IconPlus,
  IconTrash,
  IconEye,
  IconLoader2,
  IconX,
  IconClock,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { BrandMultiSelect } from '../components/campaign/BrandMultiSelect';
import { CampaignAudiencePreview } from '../components/campaign/CampaignAudiencePreview';
import { CampaignFilterEditor } from '../components/campaign/CampaignFilterEditor';
import {
  BrandGroupsDialog,
  SaveGroupDialog,
  type BrandGroup,
} from '../components/campaign/BrandGroupsDialog';
import PageHeader from '../components/PageHeader';
import { TargetedSendCard } from '../components/TargetedSendCard';
import { InboxVariantEditor } from '../components/campaign-channels/InboxVariantEditor';
import {
  EMPTY_INBOX_VARIANT,
  InboxVariant,
  inboxVariantError,
} from '../components/campaign-channels/inbox-variant';
import { PushVariantEditor } from '../components/campaign-channels/PushVariantEditor';
import {
  EMPTY_PUSH_VARIANT,
  PushVariant,
  pushVariantError,
} from '../components/campaign-channels/push-variant';
import { EmailVariantEditor } from '../components/campaign-channels/EmailVariantEditor';
import {
  EMPTY_EMAIL_VARIANT,
  EmailVariant,
  emailVariantError,
  toEmailVariantPayload,
} from '../components/campaign-channels/email-variant';
import type {
  AudienceChannel,
  CampaignFilterInput,
} from '../features/campaign-targeting/contracts';
import {
  bindPreview,
  isPreviewCurrent,
  previewCountLabel,
  publicPreviewErrorMessage,
  sendPayload,
  type BoundCampaignAudiencePreview,
  type CampaignAudienceMode,
  type CampaignAudiencePreviewResponse,
} from '../features/campaign-targeting/preview-state';

interface CampaignMvno {
  id: string;
  label: string;
  database: string;
  dataset: string;
  supportsActivityFilter: boolean;
  campaignAudienceMode?: CampaignAudienceMode;
  supportsCampaignFilters?: boolean;
}

const EMPTY_CAMPAIGN_FILTER: CampaignFilterInput = {
  version: 1,
  logic: 'AND',
  rules: [],
};

/** DEV-1950 (FR004/FR005) — one managed test recipient, as the API returns
 * it. Full CRUD lives on TestRecipientsPage; this page only lists/selects. */
interface TestRecipient {
  id: string;
  label: string;
  msisdn: string | null;
  email: string | null;
  appUserUuid: string | null;
  accountUuid: string | null;
  mvnoId: string | null;
  enabled: boolean;
}

interface SkippedTestRecipient {
  id: string;
  label: string;
  reason: string;
}

/** Single-choice channel picker. All four channels ship. */
const CHANNEL_OPTIONS = [
  { value: 'SMS', label: 'SMS', enabled: true },
  { value: 'INBOX', label: 'In-App Inbox', enabled: true },
  { value: 'PUSH', label: 'Push', enabled: true },
  { value: 'EMAIL', label: 'Email', enabled: true },
] as const;

const getApiErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error?.message ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

/** "YYYY-MM-DDTHH:mm" in SAST for `at` — used as a datetime-local input's
 * `min` attribute (Codex fix C21a). Timezone EXPLICIT, same reasoning as
 * `scheduledAtIso`'s fixed +02:00 offset: SA has no DST, so this is always
 * correct regardless of the operator's browser zone or the host's clock. */
function toSastDatetimeLocalMin(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

// Hoisted to module scope (rather than declared inside the component) so
// TargetedSendCard can import and share the same 160-char SMS limit.
export const MAX_MESSAGE_LENGTH = 160;

export function CampaignsPage() {
  const { hasWriteAccess } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [messages, setMessages] = useState<string[]>(['']);
  const [abTestEnabled, setAbTestEnabled] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendWarning, setSendWarning] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [testSent, setTestSent] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testError, setTestError] = useState('');
  const [testSuccess, setTestSuccess] = useState('');
  /** Page-level warning: survives the send dialog closing, unlike sendWarning. */
  const [pageWarning, setPageWarning] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [filters, setFilters] = useState<CampaignFilterInput>(
    EMPTY_CAMPAIGN_FILTER,
  );
  const [filtersValid, setFiltersValid] = useState(true);
  const [activeWithinDays, setActiveWithinDays] = useState<number | null>(null);
  const [filterEditorVersion, setFilterEditorVersion] = useState(0);
  const [audiencePreview, setAudiencePreview] =
    useState<BoundCampaignAudiencePreview | null>(null);
  const [previewEvidence, setPreviewEvidence] =
    useState<CampaignAudiencePreviewResponse | null>(null);
  const [availableMvnos, setAvailableMvnos] = useState<CampaignMvno[]>([]);
  // DEV-1948: the audience is a SET of brands. One brand is just the
  // single-element case — every payload sends `mvnoIds`, never `mvnoId`.
  const [selectedMvnoIds, setSelectedMvnoIds] = useState<string[]>([]);
  // DEV-1949: saved brand groups, shared across operators.
  const [brandGroups, setBrandGroups] = useState<BrandGroup[]>([]);
  const [saveGroupOpen, setSaveGroupOpen] = useState(false);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  /** Which selected brand the `{{brand}}` preview renders for. Only meaningful
   * with more than one brand selected; reconciled on every selection change so
   * it can never name a brand that is no longer in the audience. */
  const [previewBrandId, setPreviewBrandId] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] =
    useState<AudienceChannel>('SMS');
  const smsEnabled = selectedChannel === 'SMS';
  const inboxEnabled = selectedChannel === 'INBOX';
  const [inboxVariant, setInboxVariant] =
    useState<InboxVariant>(EMPTY_INBOX_VARIANT);
  const pushEnabled = selectedChannel === 'PUSH';
  const [pushVariant, setPushVariant] =
    useState<PushVariant>(EMPTY_PUSH_VARIANT);
  const emailEnabled = selectedChannel === 'EMAIL';
  const [emailVariant, setEmailVariant] =
    useState<EmailVariant>(EMPTY_EMAIL_VARIANT);
  // DEV-1950 (FR004/FR005): the managed test-recipient pool, and which of
  // its ENABLED rows are currently selected for the next test send.
  // `selectedTestRecipientIds` starts as "every enabled recipient" and stays
  // that way until the operator unchecks one — see `testRecipientIdsForSend`
  // for why that default state is sent as an OMITTED selector, not an
  // explicit list.
  const [testRecipients, setTestRecipients] = useState<TestRecipient[]>([]);
  const [selectedTestRecipientIds, setSelectedTestRecipientIds] = useState<
    string[]
  >([]);
  const [loadingTestRecipients, setLoadingTestRecipients] = useState(false);
  const [skippedTestRecipients, setSkippedTestRecipients] = useState<
    SkippedTestRecipient[]
  >([]);

  // DEV-1953: retain the existing compose behavior where scheduling is shown
  // only for a multi-channel/multi-brand campaign. `scheduledAtLocal` is the raw
  // `<input type="datetime-local">` value ("YYYY-MM-DDTHH:mm", no timezone) —
  // it is ALWAYS interpreted as SAST (Africa/Johannesburg), never the
  // operator's browser zone, by appending the fixed `+02:00` offset in
  // `scheduledAtIso` below. South Africa has no daylight-saving time, so a
  // fixed offset is correct year-round.
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const selectedMvnos = availableMvnos.filter((mvno) =>
    selectedMvnoIds.includes(mvno.id),
  );
  const selectedAudienceModes = new Set(
    selectedMvnos.map((mvno) => mvno.campaignAudienceMode ?? 'legacy-live'),
  );
  const audienceMode: CampaignAudienceMode | null =
    selectedAudienceModes.size === 1
      ? ([...selectedAudienceModes][0] as CampaignAudienceMode)
      : null;
  const hasMixedAudienceModes = selectedAudienceModes.size > 1;
  const brandLabels = selectedMvnos.map((mvno) => mvno.label);
  // This flag controls test-send and scheduling UI only. Full audience sends,
  // including one-brand SMS, use the durable multi-channel endpoint so an
  // immediate send can carry its preview binding. The separate hand-entered
  // TargetedSendCard remains the direct-send path.
  const multiChannel =
    inboxEnabled || pushEnabled || emailEnabled || selectedMvnoIds.length > 1;
  /** `{{brand}}` renders per brand; the tabs below pick which one is shown. */
  const previewBrand =
    selectedMvnos.find((mvno) => mvno.id === previewBrandId) ??
    selectedMvnos[0];
  /** SMS length is validated against the LONGEST selected brand label — the
   * worst case is the one that overruns 160 characters in production. */
  const longestBrandLabel = brandLabels.reduce(
    (longest, label) => (label.length > longest.length ? label : longest),
    '',
  );
  const canEditGroups = hasWriteAccess('campaigns-brand-groups');

  // Codex fix C5: scheduling only exists on the multi-channel path — the
  // moment the selection drops to SMS-only, the Schedule toggle disappears
  // from the confirm dialog (guarded by `multiChannel &&` below), but
  // `sendMode`/`scheduledAtLocal` are React state and would otherwise
  // persist unchanged. Without this reset, re-checking a channel later (or
  // the confirm dialog re-rendering mid-flow) could leave `sendMode` stuck
  // on 'schedule' with no visible way to change it back, and any code that
  // forgot to gate on `multiChannel` too (the warning-alert text below did,
  // before this fix) would read stale scheduling intent.
  useEffect(() => {
    if (!multiChannel) {
      setSendMode('now');
      setScheduledAtLocal('');
    }
  }, [multiChannel]);

  /** `scheduledAtLocal` + fixed SAST offset -> a real ISO8601 instant, or
   * null when not scheduling / no time chosen yet / not on the multi-channel
   * path at all (defensive — see the `useEffect` above; `scheduledAtIso` is
   * only ever read by `handleConfirmMultiChannelSend`, but gating it here
   * too keeps this value truthful on its own terms). See the state's own
   * comment for why the offset is hardcoded rather than read from the
   * browser. */
  const scheduledAtIso =
    multiChannel && sendMode === 'schedule' && scheduledAtLocal
      ? `${scheduledAtLocal}:00+02:00`
      : null;
  const selectedChannels: AudienceChannel[] = [selectedChannel];
  const targetingSelection = {
    audienceMode: audienceMode ?? 'legacy-live',
    mvnoIds: selectedMvnoIds,
    channels: selectedChannels,
    filters,
    activeWithinDays,
  };
  const currentAudiencePreview = isPreviewCurrent(
    audiencePreview,
    targetingSelection,
  );
  const audiencePayload = () => ({ mvnoIds: selectedMvnoIds });

  const DYNAMIC_FIELDS = [
    {
      key: '{{spend_requirement}}',
      label: 'Spend Requirement',
      description: 'R amount',
    },
    { key: '{{spend_30_day}}', label: '30-Day Spend', description: 'R amount' },
    { key: '{{reward_value}}', label: 'Reward Value', description: 'MB or GB' },
    {
      key: '{{first_name}}',
      label: 'First Name',
      description: 'Customer first name',
    },
    { key: '{{surname}}', label: 'Surname', description: 'Customer surname' },
    { key: '{{brand}}', label: 'Brand', description: 'Customer brand' },
  ];
  // Nova CDC all-customer audiences only ever expose first_name/surname/brand —
  // the spend/reward fields belonged to the removed legacy BigQuery audience
  // flow.
  const visibleDynamicFields = DYNAMIC_FIELDS.filter(
    (field) =>
      field.key === '{{first_name}}' ||
      field.key === '{{surname}}' ||
      field.key === '{{brand}}',
  );

  const hasDynamicField = (message: string) =>
    DYNAMIC_FIELDS.some((field) => message.includes(field.key));

  const insertDynamicField = (field: string, messageIndex: number = 0) => {
    const currentMessage = messages[messageIndex] || '';
    const cursorPos =
      (document.activeElement as HTMLInputElement)?.selectionStart ||
      currentMessage.length;
    const newMessage =
      currentMessage.slice(0, cursorPos) +
      field +
      currentMessage.slice(cursorPos);
    if (newMessage.length <= MAX_MESSAGE_LENGTH) {
      const newMessages = [...messages];
      newMessages[messageIndex] = newMessage;
      setMessages(newMessages);
      invalidateTestGate();
      setTimeout(() => {
        const textFields = document.querySelectorAll(
          'textarea[aria-label*="SMS Message"]',
        );
        const textField = textFields[messageIndex] as HTMLTextAreaElement;
        if (textField) {
          textField.focus();
          const newPos = cursorPos + field.length;
          textField.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  };

  const handlePreviewCount = async () => {
    setError('');
    setSuccess('');
    setAudiencePreview(null);
    setPreviewEvidence(null);
    setPreviewCount(null);
    if (selectedMvnoIds.length === 0) {
      setError('Please select at least one brand');
      return;
    }
    if (selectedChannels.length === 0) {
      setError('Please select at least one channel');
      return;
    }
    if (hasMixedAudienceModes) {
      setError(
        'Selected brands use different audience modes. Choose only snapshot-enabled brands or only legacy-live brands.',
      );
      return;
    }
    if (audienceMode === 'snapshot' && !filtersValid) {
      setError('Fix the audience targeting rules before previewing');
      return;
    }
    setLoadingPreview(true);
    try {
      const response = await api.post(
        '/campaigns/multi-channel/preview-counts',
        {
          ...audiencePayload(),
          channels: selectedChannels,
          ...(audienceMode === 'snapshot' ? { filters } : { activeWithinDays }),
        },
      );
      const previewResponse = response.data as CampaignAudiencePreviewResponse;
      setPreviewEvidence(previewResponse);
      const brands = previewResponse.brands;
      const boundPreview = bindPreview(previewResponse, targetingSelection);
      if (!boundPreview) {
        const failedBrands = brands.filter((brand) => brand.error);
        setError(
          failedBrands.length > 0
            ? `Preview: ${failedBrands
                .map(
                  (brand) =>
                    `${brand.brandLabel} could not be counted (${publicPreviewErrorMessage(brand)})`,
                )
                .join(' | ')}`
            : publicPreviewErrorMessage({}),
        );
        return;
      }
      setAudiencePreview(boundPreview);
      // The gate below ("send a test first") is about blast radius, so the
      // largest single brand-and-channel audience is the honest number to gate
      // on — a brand that could not be counted contributes nothing rather than
      // pretending to be zero-risk.
      setPreviewCount(
        brands
          .flatMap((brand) => brand.channels || [])
          .reduce((largest, entry) => Math.max(largest, entry.eligible), 0),
      );
      setSuccess(
        `Preview: ${brands
          .map((brand) =>
            brand.error
              ? `${brand.brandLabel} could not be counted (${publicPreviewErrorMessage(brand)})`
              : `${brand.brandLabel} — ${(brand.channels || [])
                  .map((entry) => `${entry.channel} ${entry.eligible}`)
                  .join(', ')}`,
          )
          .join(' | ')}`,
      );
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setError(
        publicPreviewErrorMessage({
          errorCode:
            (typeof apiError === 'object' ? apiError?.code : undefined) ??
            err?.response?.data?.code ??
            err?.code,
          error:
            (typeof apiError === 'object' ? apiError?.message : apiError) ??
            err?.response?.data?.message ??
            err?.message,
        }),
      );
      setPreviewCount(null);
      setAudiencePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    loadAvailableMvnos();
  }, []);
  useEffect(() => {
    loadTestRecipients();
  }, []);
  useEffect(() => {
    loadBrandGroups();
  }, []);

  /**
   * DEV-1950 (FR005): what to send as `testRecipientIds`. `undefined` when
   * the selection is still exactly "every enabled recipient" (the untouched
   * default) — an OMITTED selector, not an explicit list naming all of them,
   * so the server's own default-and-legacy-fallback logic
   * (`CampaignsService.resolveTestRecipientPool`) still applies if the
   * managed table turns out to hold nothing usable. Once the operator
   * unchecks anything (including down to zero), the explicit array is sent
   * exactly as selected — an explicit empty selection sends to nobody rather
   * than silently falling back to the legacy recipients.
   */
  const testRecipientIdsForSend = (): string[] | undefined => {
    const enabledIds = testRecipients.filter((r) => r.enabled).map((r) => r.id);
    if (enabledIds.length === 0) return undefined;
    const isUntouchedDefault =
      selectedTestRecipientIds.length === enabledIds.length &&
      enabledIds.every((id) => selectedTestRecipientIds.includes(id));
    return isUntouchedDefault ? undefined : selectedTestRecipientIds;
  };

  const toggleTestRecipient = (id: string) => {
    setSelectedTestRecipientIds((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id],
    );
  };

  const handleBrandSelectionChange = (ids: string[]) => {
    setSelectedMvnoIds(ids);
    resetPreview();
    // The `{{brand}}` preview must always name a brand that is still selected.
    setPreviewBrandId((current) =>
      current && ids.includes(current) ? current : (ids[0] ?? null),
    );
  };

  /**
   * Resets the composer after a send that is DONE — fully or partially.
   *
   * Called on a partial send too, deliberately: those recipients are already
   * queued, so leaving the form armed invites a re-send that double-delivers
   * to everyone the first attempt reached.
   */
  const clearComposer = () => {
    setCampaignName('');
    setMessages(['']);
    setAbTestEnabled(false);
    setInboxVariant(EMPTY_INBOX_VARIANT);
    setPushVariant(EMPTY_PUSH_VARIANT);
    setEmailVariant(EMPTY_EMAIL_VARIANT);
    setActiveWithinDays(null);
    invalidateFilterAndPreview();
    setTestSent(false);
    setTestSuccess('');
    setSkippedTestRecipients([]);
    setSendDialogOpen(false);
    setSendMode('now');
    setScheduledAtLocal('');
    // Codex fix C21(c): closing the confirm dialog now happens ONLY here —
    // i.e. only on an outcome that actually clears the composer (success, or
    // "partially sent, don't retry"). A rejection (schedule validation, or
    // any other 400) leaves the confirm dialog open with the error rendered
    // inside IT, not silently in the send dialog sitting behind it.
    setConfirmDialogOpen(false);
  };

  /** Clear targeting only after an outcome that has made this composition
   * non-retryable. Preview invalidation during ordinary edits must never reset
   * the operator's rules. */
  const invalidateFilterAndPreview = () => {
    setFilters(EMPTY_CAMPAIGN_FILTER);
    setFiltersValid(true);
    setFilterEditorVersion((current) => current + 1);
    setPreviewCount(null);
    setAudiencePreview(null);
    setPreviewEvidence(null);
  };

  /**
   * Editing content after a green test invalidates the test.
   *
   * The gate exists so nobody blasts an untested payload; leaving testSent
   * true after an edit means the operator tested payload A and sent payload B,
   * which is the exact failure the gate was added to prevent. This affected
   * the SMS message textareas too (pre-existing), so both paths route through
   * here.
   */
  const invalidateTestGate = () => {
    if (testSent || testSuccess) {
      setTestSent(false);
      setTestSuccess('');
      setTestError('');
    }
  };

  /** Any change to brand or channel selection invalidates the counts AND the
   * test-sent gate: both were established against the previous selection. */
  const resetPreview = () => {
    setPreviewCount(null);
    setAudiencePreview(null);
    setPreviewEvidence(null);
    setSuccess('');
    setError('');
    setPageWarning('');
    setTestSent(false);
    setTestSuccess('');
    setTestError('');
  };

  const loadAvailableMvnos = async () => {
    try {
      const response = await api.get('/campaigns/available-mvnos');
      setAvailableMvnos(response.data.mvnos || []);
    } catch (err: any) {
      console.error('Failed to load available MVNOs:', err);
    }
  };

  /**
   * DEV-1949. Groups are readable by every campaigns operator, so this runs on
   * mount for everyone; only the mutation buttons are gated on write access.
   *
   * A failure is logged, not surfaced as a page error: groups are a shortcut
   * for selecting brands, and the picker still works brand-by-brand without
   * them.
   */
  const loadBrandGroups = async () => {
    try {
      const response = await api.get('/campaigns/brand-groups');
      setBrandGroups(response.data.groups || []);
    } catch (err: any) {
      console.error('Failed to load brand groups:', err);
      setBrandGroups([]);
    }
  };

  /**
   * DEV-1950 (FR004/FR005). Loads every managed test recipient (enabled or
   * not — the checkbox list below filters to enabled itself, so an operator
   * who just disabled one still sees it, greyed out, rather than have it
   * vanish unexplained) and defaults the selection to "every enabled one",
   * matching the server-side default when no selection is sent at all.
   *
   * A failure here is logged, not surfaced as a page error: the send-test
   * flow still works via the legacy env-config/hard-coded fallback when the
   * managed list cannot be loaded, same as when it is genuinely empty.
   */
  const loadTestRecipients = async () => {
    setLoadingTestRecipients(true);
    try {
      const response = await api.get('/campaigns/test-recipients');
      const recipients: TestRecipient[] = response.data.recipients || [];
      setTestRecipients(recipients);
      setSelectedTestRecipientIds(
        recipients.filter((r) => r.enabled).map((r) => r.id),
      );
    } catch (err: any) {
      console.error('Failed to load test recipients:', err);
      setTestRecipients([]);
      setSelectedTestRecipientIds([]);
    } finally {
      setLoadingTestRecipients(false);
    }
  };

  /** Whole-compose validation across every selected channel. */
  const validateCampaign = (): string | null => {
    if (selectedChannels.length === 0)
      return 'Please select at least one channel';
    if (smsEnabled) {
      const smsError = validateMessages();
      if (smsError) return smsError;
    }
    if (inboxEnabled) {
      // The editor's own message, verbatim: it names the reason (a missing
      // field, a length, or a non-https image URL) that a generic string would
      // throw away.
      const inboxError = inboxVariantError(inboxVariant);
      if (inboxError) return inboxError;
    }
    if (pushEnabled) {
      // The push editor's own message, verbatim: it already names the reason
      // (length, or the {{first_name}} refusal), which a generic string would
      // throw away.
      const pushError = pushVariantError(pushVariant);
      if (pushError) return pushError;
    }
    if (emailEnabled) {
      const emailError = emailVariantError(emailVariant);
      if (emailError) return emailError;
    }
    return null;
  };

  const validateMessages = (): string | null => {
    const validMessages = messages.filter((m) => m.trim().length > 0);
    if (validMessages.length === 0) return 'Please enter at least one message';
    if (validMessages.length > 3)
      return 'Maximum 3 message variants allowed for AB testing';
    for (let i = 0; i < validMessages.length; i++) {
      const msg = validMessages[i];
      const hasDynamicFields = hasDynamicField(msg);
      if (hasDynamicFields) {
        // Worst case across the selected brands, not the brand whose preview
        // tab happens to be open: the longest label is the one that overruns.
        const preview = getMessagePreviewForMessage(
          msg,
          longestBrandLabel || undefined,
        );
        if (preview.length > MAX_MESSAGE_LENGTH)
          return `Message variant ${i + 1} preview exceeds ${MAX_MESSAGE_LENGTH} characters (longest brand name "${longestBrandLabel}"). Preview length: ${preview.length}`;
      } else {
        if (msg.length > MAX_MESSAGE_LENGTH)
          return `Message variant ${i + 1} exceeds ${MAX_MESSAGE_LENGTH} characters. Current length: ${msg.length}`;
      }
    }
    return null;
  };

  const getMessagePreviewForMessage = (
    msg: string,
    brandLabel: string = previewBrand?.label || 'megsApp',
  ): string => {
    if (!msg) return '';
    let preview = msg;
    preview = preview.replace(
      /\{\{spend_requirement\}\}/g,
      formatSpendValue(500.0),
    );
    preview = preview.replace(/\{\{spend_30_day\}\}/g, formatSpendValue(300.0));
    preview = preview.replace(/\{\{reward_value\}\}/g, formatRewardValue(1500));
    preview = preview.replace(/\{\{first_name\}\}/g, 'Jane');
    preview = preview.replace(/\{\{surname\}\}/g, 'Doe');
    preview = preview.replace(/\{\{brand\}\}/g, brandLabel);
    return preview;
  };

  const handleSendClick = () => {
    if (!campaignName.trim()) {
      setSendError('Please enter a campaign name');
      return;
    }
    const validationError = validateCampaign();
    if (validationError) {
      setSendError(validationError);
      return;
    }
    const shouldRequireTest = previewCount !== null && previewCount >= 4;
    if (shouldRequireTest && !testSent) {
      setSendError('Please send a test first before scheduling the campaign');
      return;
    }
    setSendError('');
    setConfirmDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!campaignName.trim()) {
      setTestError('Please enter a campaign name');
      return;
    }
    const validationError = validateCampaign();
    if (validationError) {
      setTestError(validationError);
      return;
    }
    if (multiChannel) {
      await handleMultiChannelTest();
      return;
    }
    const testMessage = messages[0]?.trim() || '';
    if (!testMessage) {
      setTestError('Please enter a message');
      return;
    }
    setSendingTest(true);
    setTestError('');
    setTestSuccess('');
    setSkippedTestRecipients([]);
    // DEV-1957: a new test also clears the previous SEND banners. Otherwise
    // every action stacked one more never-dismissed alert inside the dialog
    // and pushed the Send/Schedule footer further out of reach.
    setSendError('');
    setSendWarning('');
    setSendSuccess('');
    try {
      const response = await api.post('/campaigns/send-test-sms', {
        campaignName: campaignName.trim(),
        message: testMessage,
        // Only reachable with exactly one brand selected — see `multiChannel`.
        mvnoId: selectedMvnoIds[0] || undefined,
        testRecipientIds: testRecipientIdsForSend(),
      });
      setSkippedTestRecipients(response.data.skippedRecipients || []);
      if (response.data.success) {
        const sent = response.data.sent ?? 0;
        setTestSuccess(
          `Test SMS sent successfully to ${sent} test recipient${sent !== 1 ? 's' : ''}`,
        );
        setTestSent(true);
        setTestError('');
      } else if (response.data.sent > 0) {
        // The test send exercises the same ledger path as the full campaign —
        // a tracking failure here predicts one at full scale. Keep the
        // campaign gate closed (testSent stays false).
        setTestError(
          `Test SMS reached ${response.data.sent} recipient(s) but delivery tracking failed — resolve this before sending the campaign.`,
        );
      } else {
        setTestError('Failed to send test SMS');
      }
    } catch (err: any) {
      setTestError(getApiErrorMessage(err, 'Failed to send test SMS'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleMultiChannelTest = async () => {
    setSendingTest(true);
    setTestError('');
    setTestSuccess('');
    setSkippedTestRecipients([]);
    // DEV-1957: see handleSendTest — clear stale send banners too.
    setSendError('');
    setSendWarning('');
    setSendSuccess('');
    try {
      const response = await api.post('/campaigns/multi-channel/send-test', {
        campaignName: campaignName.trim(),
        ...audiencePayload(),
        testRecipientIds: testRecipientIdsForSend(),
        channels: {
          ...(smsEnabled
            ? { sms: { message: messages[0]?.trim() || '' } }
            : {}),
          ...(inboxEnabled ? { inbox: inboxVariant } : {}),
          ...(pushEnabled ? { push: pushVariant } : {}),
          ...(emailEnabled
            ? { email: toEmailVariantPayload(emailVariant) }
            : {}),
        },
      });
      const results: Array<Record<string, any>> = response.data.results || [];
      // SMS test sends run per brand; INBOX/PUSH/EMAIL run once. The brand
      // label is therefore named only where the API returned one — otherwise
      // the summary would invent a brand for a brand-agnostic result.
      const summary = results
        .map((result) => {
          const scope = result.brandLabel
            ? `${result.brandLabel} ${result.channel}`
            : result.channel;
          const primary = `${scope}: ${result.success === false ? 'FAILED' : 'ok'}`;
          if (!result.companionPush) return primary;
          const companion = result.companionPush;
          const detail = companion.success === false ? 'FAILED' : 'ok';
          return `${primary}; companion PUSH: ${detail} (${companion.published ?? 0} sent, ${companion.skippedNoSubscription ?? 0} without notifications)`;
        })
        .join(', ');
      const companionFailures = results
        .filter((result) => result.companionPush?.success === false)
        .map((result) => result.companionPush?.error)
        .filter(Boolean);
      // FR005: every channel's own skip list, deduplicated by recipient id —
      // the same managed recipient can be reported skipped by more than one
      // channel (e.g. no msisdn AND no email), and the operator only needs
      // to see it once.
      const skippedById = new Map<string, SkippedTestRecipient>();
      for (const result of results) {
        for (const skipped of (result.skippedRecipients ||
          []) as SkippedTestRecipient[]) {
          if (!skippedById.has(skipped.id))
            skippedById.set(skipped.id, skipped);
        }
      }
      setSkippedTestRecipients([...skippedById.values()]);
      if (response.data.success) {
        setTestSuccess(
          `Test send completed (${summary}). You can now schedule the campaign.`,
        );
        if (companionFailures.length > 0) {
          setSendWarning(
            `The Inbox test succeeded, but its optional companion push failed: ${companionFailures.join('; ')}`,
          );
        }
        setTestSent(true);
      } else {
        // A test whose outcome could not be established keeps the campaign
        // gate CLOSED: the canary exists to be trusted.
        setTestError(
          `Test send did not fully succeed (${summary}) — resolve this before sending the campaign.`,
        );
      }
    } catch (err: any) {
      setTestError(getApiErrorMessage(err, 'Failed to send test'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleConfirmMultiChannelSend = async () => {
    // Codex fix C21(c): the confirm dialog is NOT closed here anymore — only
    // `clearComposer()` closes it, and only on an outcome that actually
    // clears the composer. A rejection (e.g. the WASPA/lead-time 400s a
    // scheduled send can get) leaves this dialog open with the error visible
    // in it, instead of silently landing in the send dialog behind it.
    setSending(true);
    setSendError('');
    setSendWarning('');
    setSendSuccess('');
    // DEV-1957: drop the test banners as the real send starts — they describe
    // the previous action and only add height to this dialog. `testSent` is
    // deliberately untouched: it is the gate, not a banner.
    setTestError('');
    setTestSuccess('');
    setPageWarning('');
    try {
      const validMessages = messages
        .filter((m) => m.trim().length > 0)
        .map((m) => m.trim());
      const targeting = sendPayload(
        audiencePreview,
        targetingSelection,
        scheduledAtIso ? 'schedule' : 'now',
      );
      const response = await api.post('/campaigns/multi-channel/send', {
        campaignName: campaignName.trim(),
        ...audiencePayload(),
        ...targeting,
        channels: {
          ...(smsEnabled ? { sms: { messages: validMessages } } : {}),
          ...(inboxEnabled ? { inbox: inboxVariant } : {}),
          ...(pushEnabled ? { push: pushVariant } : {}),
          ...(emailEnabled
            ? { email: toEmailVariantPayload(emailVariant) }
            : {}),
        },
        ...(scheduledAtIso ? { scheduledAt: scheduledAtIso } : {}),
      });
      // DEV-1948: the send fans out per brand, so EVERY outcome below is
      // aggregated across brands and every failure names the brand it belongs
      // to — "PUSH failed" across a five-brand send is not actionable.
      const campaigns: Array<{
        mvnoId: string;
        brandLabel: string;
        success: boolean;
        campaignId?: string;
        jobs?: Array<{ channel: string; jobId: string; queued: number }>;
        failures?: Array<{ channel: string; error: string }>;
        warnings?: string[];
        status?: string;
        scheduledAt?: string;
        backgroundPreparation?: boolean;
      }> = response.data.campaigns || [];
      const jobs = campaigns.flatMap((campaign) => campaign.jobs || []);
      const failures = campaigns.flatMap((campaign) =>
        (campaign.failures || []).map((failure) => ({
          ...failure,
          brandLabel: campaign.brandLabel,
        })),
      );
      const warnings = campaigns.flatMap((campaign) => campaign.warnings || []);
      const queuedTotal = jobs.reduce(
        (total, job) => total + (job.queued || 0),
        0,
      );
      const summary = campaigns
        .map((campaign) => {
          const queuedByChannel = (campaign.jobs || []).reduce<
            Record<string, number>
          >((totals, job) => {
            totals[job.channel] =
              (totals[job.channel] || 0) + (job.queued || 0);
            return totals;
          }, {});
          const perChannel = Object.entries(queuedByChannel)
            .map(([channel, queued]) => `${channel} ${queued}`)
            .join(', ');
          return `${campaign.brandLabel}: ${perChannel || 'nothing queued'}`;
        })
        .join(' | ');
      const jobCount = `${jobs.length} job${jobs.length !== 1 ? 's' : ''}`;
      const brandCount = `${campaigns.length} brand${campaigns.length !== 1 ? 's' : ''}`;

      // DEV-1953: a scheduled response never carries jobs (Phase 2 has not
      // run yet) — its own branch, before the immediate-send outcome logic
      // below ever asks "how many jobs queued".
      if (
        campaigns.some((campaign) => campaign.backgroundPreparation === true)
      ) {
        const message = `Campaign accepted across ${brandCount}. Audience preparation and durable inbox delivery are continuing in the background; you can leave this page safely. Track preparation under Campaigns → Scheduled Campaigns and delivery under Campaigns → Send Jobs.`;
        setSuccess(message);
        setSendSuccess(message);
        if (warnings.length > 0) {
          setPageWarning(warnings.join(' '));
        }
        clearComposer();
        return;
      }

      if (campaigns.some((campaign) => campaign.status === 'SCHEDULED')) {
        const scheduledAt = campaigns.find(
          (campaign) => campaign.scheduledAt,
        )?.scheduledAt;
        const releaseAt = scheduledAt
          ? new Date(scheduledAt).toLocaleString('en-ZA', {
              timeZone: 'Africa/Johannesburg',
            })
          : scheduledAtLocal;
        const message = `Scheduled for release at ${releaseAt} SAST across ${brandCount} (${campaigns.map((campaign) => campaign.brandLabel).join(', ')}). Track it under Campaigns → Scheduled Campaigns.`;
        setSuccess(message);
        setSendSuccess(message);
        if (warnings.length > 0) {
          setPageWarning(warnings.join(' '));
        }
        clearComposer();
        return;
      }

      // The outcome is derived from the BODY, not from success alone. The
      // backend returns success:false for a PARTIAL send too — jobs already
      // queued plus a channel that failed — and reporting that as "nothing
      // queued" would tell an operator to re-send a campaign that is already
      // in flight.
      if (response.data.success) {
        const message = `Queued for durable delivery (${summary}) across ${jobCount} and ${brandCount}. Sending continues even if you leave this page. Track progress or cancel under Campaigns → Send Jobs.`;
        // Page-level too: the dialog closes on success, and everything inside
        // it closes with it.
        setSuccess(message);
        setSendSuccess(message);
        if (warnings.length > 0) {
          setPageWarning(warnings.join(' '));
        }
        clearComposer();
      } else if (queuedTotal > 0) {
        const failed =
          failures.length > 0
            ? failures
                .map(
                  (failure) =>
                    `${failure.brandLabel} ${failure.channel} failed (${failure.error})`,
                )
                .join('; ')
            : 'another brand or channel failed';
        const campaignIds = campaigns
          .filter((campaign) => campaign.campaignId)
          .map((campaign) => `${campaign.brandLabel} ${campaign.campaignId}`)
          .join(', ');
        const message =
          `PARTIALLY SENT — do NOT re-send. ${summary} already queued across ${jobCount} and those messages ARE being delivered; ${failed}. ` +
          `Campaigns ${campaignIds} — reconcile under Campaigns → Send Jobs before retrying, or the retry will double-send to everyone already reached.`;
        const full =
          warnings.length > 0 ? `${message} ${warnings.join(' ')}` : message;
        setPageWarning(full);
        setSendWarning(full);
        // The send is DONE, not retryable: clearing the composer and closing
        // the dialog is what stops the button being re-armed on content that
        // has already gone out.
        clearComposer();
      } else {
        // Genuinely nothing queued: safe to fix and retry, so the dialog stays
        // open with the composer intact.
        const detail = [
          ...failures.map(
            (failure) =>
              `${failure.brandLabel} ${failure.channel}: ${failure.error}`,
          ),
          ...warnings,
        ].join(' ');
        setSendError(
          `No recipients were queued. ${detail || 'Check the channel audience and try again.'}`,
        );
      }
    } catch (err: any) {
      setSendError(getApiErrorMessage(err, 'Failed to send campaign'));
    } finally {
      setSending(false);
    }
  };

  const handleConfirmSend = async () => {
    await handleConfirmMultiChannelSend();
  };

  const formatRewardValue = (value: number): string => {
    if (!value || value === 0) return '—';
    const numValue =
      typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numValue)) return '—';
    if (numValue >= 1000) {
      const gbValue = numValue / 1000;
      let formatted = gbValue.toFixed(2);
      formatted = formatted.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
      return `${formatted} GB`;
    }
    return `${numValue.toFixed(0)} MB`;
  };

  const formatSpendValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    const numValue =
      typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numValue)) return '—';
    const formatted = numValue.toFixed(2);
    return formatted.endsWith('.00')
      ? `R${formatted.slice(0, -3)}`
      : `R${formatted}`;
  };

  return (
    <div>
      <PageHeader
        title="Campaigns"
        subtitle="Select one or more brands and preview the audience mode currently enabled for them."
      />
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-1">Campaign Audience</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Snapshot-enabled brands use durable campaign profiles;
            rollout-disabled brands keep the live Nova CDC audience flow.
          </p>

          <div className="mb-6 grid gap-2 max-w-xl">
            <Label>Brands</Label>
            <BrandMultiSelect
              options={availableMvnos.map((mvno) => ({
                id: mvno.id,
                label: mvno.label,
              }))}
              selectedIds={selectedMvnoIds}
              onSelectionChange={handleBrandSelectionChange}
              groups={brandGroups.map((group) => ({
                id: group.id,
                name: group.name,
                brandIds: group.brandIds,
              }))}
              canEditGroups={canEditGroups}
              onSaveGroup={() => setSaveGroupOpen(true)}
              onManageGroups={() => setManageGroupsOpen(true)}
            />
            <p className="text-xs text-muted-foreground">
              Each selected brand gets its own campaign against its own CDC
              dataset — one send, one campaign per brand.
            </p>
            {selectedMvnoIds.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Select at least one brand to enable Preview and Send.
              </p>
            )}
          </div>

          <div className="mb-6 flex flex-col gap-2">
            <Label className="mb-2 block">Channels</Label>
            <ToggleGroup
              variant="outline"
              value={[selectedChannel]}
              onValueChange={(values) => {
                const value = values.find(
                  (candidate) => candidate !== selectedChannel,
                );
                if (!value) return;
                setSelectedChannel(value as AudienceChannel);
                resetPreview();
              }}
              aria-label="Campaign channel"
              className="flex-wrap"
            >
              {CHANNEL_OPTIONS.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  disabled={!option.enabled}
                  aria-label={option.label}
                >
                  <span>{option.label}</span>
                  {!option.enabled && (
                    <Badge variant="secondary" className="text-[10px]">
                      coming soon
                    </Badge>
                  )}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              Choose one delivery channel per campaign.
            </p>
          </div>

          {audienceMode === 'snapshot' ? (
            <div className="mb-6 max-w-3xl">
              <CampaignFilterEditor
                key={filterEditorVersion}
                channels={selectedChannels}
                onChange={setFilters}
                onValidityChange={setFiltersValid}
                onPreviewInvalidated={resetPreview}
                disabled={loadingPreview || sending}
              />
            </div>
          ) : audienceMode === 'legacy-live' ? (
            <div className="mb-6 grid max-w-sm gap-2">
              <Label>Activity Filter</Label>
              <Select
                value={activeWithinDays?.toString() ?? 'off'}
                onValueChange={(value) => {
                  setActiveWithinDays(value === 'off' ? null : Number(value));
                  resetPreview();
                }}
                disabled={loadingPreview || sending}
              >
                <SelectTrigger aria-label="Activity Filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off — all active accounts</SelectItem>
                  {[30, 60, 90, 180].map((days) => (
                    <SelectItem key={days} value={String(days)}>
                      Active within {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Live CDC counts are recalculated when the campaign is sent.
              </p>
            </div>
          ) : null}

          {hasMixedAudienceModes ? (
            <Alert variant="warning" className="mb-6">
              <AlertDescription>
                Selected brands use different audience modes. Choose only
                snapshot-enabled brands or only legacy-live brands.
              </AlertDescription>
            </Alert>
          ) : null}

          {selectedMvnoIds.length > 0 ? (
            <Alert variant="info" className="mb-6">
              <AlertDescription>
                Targeting the customers matching every audience rule for{' '}
                {brandLabels.join(', ')}.
              </AlertDescription>
            </Alert>
          ) : null}

          {previewEvidence && (
            <CampaignAudiencePreview
              preview={previewEvidence}
              showBrandErrors={false}
            />
          )}

          <div className="flex gap-4 items-center mt-6">
            <Button
              variant="outline"
              onClick={handlePreviewCount}
              disabled={
                loadingPreview ||
                selectedMvnoIds.length === 0 ||
                selectedChannels.length === 0 ||
                hasMixedAudienceModes ||
                (audienceMode === 'snapshot' && !filtersValid)
              }
            >
              <IconEye size={16} />
              {loadingPreview
                ? 'Loading...'
                : `Preview Audience${previewCount !== null ? ` (${previewCount})` : ''}`}
            </Button>
            <Button
              variant="default"
              onClick={() => setSendDialogOpen(true)}
              disabled={
                selectedMvnoIds.length === 0 ||
                previewCount === null ||
                loadingPreview ||
                selectedChannels.length === 0 ||
                hasMixedAudienceModes ||
                (audienceMode === 'snapshot' && !filtersValid) ||
                !currentAudiencePreview
              }
            >
              <IconSend size={16} />
              {`Send ${selectedChannels.join(' + ') || 'Campaign'}${previewCount !== null ? ` (${previewCount})` : ''}`}
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setError('')}
                >
                  <IconX size={14} />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mt-4">
              <AlertDescription className="flex items-center justify-between">
                {success}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setSuccess('')}
                >
                  <IconX size={14} />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {pageWarning && (
            <Alert variant="warning" className="mt-4">
              <AlertDescription className="flex items-start justify-between gap-2">
                <span>{pageWarning}</span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setPageWarning('')}
                >
                  <IconX size={14} />
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <TargetedSendCard availableMvnos={availableMvnos} />

      {/* Send Dialog */}
      <Dialog
        open={sendDialogOpen}
        onOpenChange={(open) => {
          if (!open && !sending && !sendingTest) {
            setSendDialogOpen(false);
            setCampaignName('');
            setMessages(['']);
            setAbTestEnabled(false);
            setSendError('');
            setSendSuccess('');
            setTestSent(false);
            setTestError('');
            setTestSuccess('');
            setSkippedTestRecipients([]);
            // Codex fix C5: don't let a schedule selection leak into the next
            // campaign composed in this dialog.
            setSendMode('now');
            setScheduledAtLocal('');
          }
        }}
      >
        {/* DEV-1957: the shared DialogContent primitive is centred and
            unbounded, so once the composer plus its banners outgrew the
            viewport the footer (Send/Schedule) was pushed off-screen and
            became unclickable. Bounding the height and scrolling the BODY —
            not the whole popup — keeps header and footer permanently visible.
            Same pattern as WalletFailuresPage. */}
        <DialogContent className="sm:max-w-2xl grid-rows-[auto_1fr_auto] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Send {selectedChannels.join(' + ') || 'SMS'} Campaign
            </DialogTitle>
            <DialogDescription>
              This will send to up to {previewCount !== null ? previewCount : 0}{' '}
              users per channel per brand across {selectedMvnoIds.length} brand
              {selectedMvnoIds.length !== 1 ? 's' : ''} (
              {brandLabels.join(', ')}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto py-2 -mx-4 px-4">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input
                autoFocus
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter campaign name (e.g., Q1 2024 Rewards Campaign)"
                disabled={sending || sendingTest}
              />
            </div>

            {smsEnabled && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={abTestEnabled}
                  onCheckedChange={(checked) => {
                    setAbTestEnabled(checked);
                    if (checked && messages.length === 1) setMessages(['', '']);
                    else if (!checked) setMessages([messages[0] || '']);
                    invalidateTestGate();
                  }}
                  disabled={sending || sendingTest}
                />
                <Label>Enable AB Testing (up to 3 variants)</Label>
              </div>
            )}

            {smsEnabled && (
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                  <IconInfoCircle size={14} />
                  Available dynamic fields:
                </p>
                <div className="flex gap-2 flex-wrap">
                  {visibleDynamicFields.map((field) => (
                    <Button
                      key={field.key}
                      size="xs"
                      variant="outline"
                      onClick={() => insertDynamicField(field.key, 0)}
                      disabled={sending || sendingTest}
                    >
                      {field.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* DEV-1948: `{{brand}}` renders differently per brand, so the
                preview names which brand it is showing rather than silently
                picking one. One brand needs no tabs — there is nothing to
                switch between. */}
            {smsEnabled && selectedMvnos.length > 1 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Preview {'{{brand}}'} as:
                </p>
                <Tabs
                  value={previewBrand?.id ?? selectedMvnos[0].id}
                  onValueChange={(value) => setPreviewBrandId(String(value))}
                >
                  <TabsList className="flex-wrap">
                    {selectedMvnos.map((mvno) => (
                      <TabsTrigger key={mvno.id} value={mvno.id}>
                        {mvno.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            )}

            {smsEnabled &&
              messages.map((msg, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">
                      Message Variant {index + 1}{' '}
                      {abTestEnabled &&
                        messages.length > 1 &&
                        `(~${Math.floor(100 / messages.length)}% of recipients)`}
                    </span>
                    {abTestEnabled && messages.length > 1 && index > 0 && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => {
                          setMessages(messages.filter((_, i) => i !== index));
                          invalidateTestGate();
                        }}
                        disabled={sending || sendingTest}
                      >
                        <IconTrash size={14} />
                      </Button>
                    )}
                  </div>
                  <Textarea
                    aria-label={`SMS Message Variant ${index + 1}`}
                    rows={3}
                    value={msg}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue.length <= MAX_MESSAGE_LENGTH) {
                        const newMessages = [...messages];
                        newMessages[index] = newValue;
                        setMessages(newMessages);
                        invalidateTestGate();
                      }
                    }}
                    placeholder="Enter your SMS message here... Use {{first_name}} or {{brand}} for dynamic values"
                    disabled={sending || sendingTest}
                    maxLength={MAX_MESSAGE_LENGTH}
                    className={cn(
                      (() => {
                        const hasDynamicFields = hasDynamicField(msg);
                        if (hasDynamicFields)
                          return getMessagePreviewForMessage(msg).length >
                            MAX_MESSAGE_LENGTH
                            ? 'border-destructive'
                            : '';
                        return msg.length > MAX_MESSAGE_LENGTH
                          ? 'border-destructive'
                          : '';
                      })(),
                    )}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => {
                      const previewLength =
                        getMessagePreviewForMessage(msg).length;
                      const hasDynamicFields =
                        msg.includes('{{spend_requirement}}') ||
                        msg.includes('{{spend_30_day}}') ||
                        msg.includes('{{reward_value}}');
                      if (hasDynamicFields)
                        return `Template: ${msg.length} chars | Preview: ${previewLength} chars (actual length varies per recipient)`;
                      return msg.length > MAX_MESSAGE_LENGTH
                        ? `Message exceeds ${MAX_MESSAGE_LENGTH} characters (${msg.length}/${MAX_MESSAGE_LENGTH})`
                        : `${msg.length}/${MAX_MESSAGE_LENGTH} characters`;
                    })()}
                  </p>
                  {msg && hasDynamicField(msg) && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-bold mb-1">Preview:</p>
                      <p className="text-sm font-mono whitespace-pre-wrap">
                        {getMessagePreviewForMessage(msg)}
                      </p>
                    </div>
                  )}
                </div>
              ))}

            {smsEnabled && abTestEnabled && messages.length < 3 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMessages([...messages, ''])}
                disabled={sending || sendingTest}
              >
                <IconPlus size={14} />
                Add Variant {messages.length + 1}
              </Button>
            )}

            {inboxEnabled && (
              <InboxVariantEditor
                value={inboxVariant}
                onChange={(variant) => {
                  setInboxVariant(variant);
                  invalidateTestGate();
                }}
                disabled={sending || sendingTest}
              />
            )}

            {pushEnabled && (
              <PushVariantEditor
                value={pushVariant}
                onChange={(variant) => {
                  setPushVariant(variant);
                  // Editing push content after a green test invalidates it, for
                  // the same reason the SMS and inbox editors do: the gate
                  // exists so nobody blasts an untested payload.
                  invalidateTestGate();
                }}
                disabled={sending || sendingTest}
              />
            )}

            {emailEnabled && (
              <EmailVariantEditor
                value={emailVariant}
                onChange={(variant) => {
                  setEmailVariant(variant);
                  invalidateTestGate();
                }}
                disabled={sending || sendingTest}
              />
            )}

            <Separator />

            {/* DEV-1950 (FR004/FR005): pick which managed test recipients the
                next test send goes to. Only ENABLED recipients are offered —
                a disabled one is retired from routine test sends, not merely
                hidden. Empty pool falls back to the legacy env-config/
                hard-coded set entirely server-side; this page needs no
                special-case UI for that, it just shows nothing to pick. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Test Recipients</Label>
                {/* Codex fix C21(e): a raw <a href> navigates the SPA with a
                    full page load, discarding whatever campaign is currently
                    composed in this dialog (unsaved name/messages/schedule
                    selection all gone). The router Link does an in-app
                    navigation instead. */}
                <Link
                  to="/campaigns/test-recipients"
                  className="text-xs text-muted-foreground underline underline-offset-2"
                >
                  Manage recipients
                </Link>
              </div>
              {loadingTestRecipients && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <IconLoader2 size={14} className="animate-spin" /> Loading
                  test recipients...
                </div>
              )}
              {!loadingTestRecipients &&
                testRecipients.filter((r) => r.enabled).length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No managed test recipients configured — the legacy
                    hard-coded/env test set will be used.
                  </p>
                )}
              {!loadingTestRecipients &&
                testRecipients.filter((r) => r.enabled).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {selectedTestRecipientIds.length} of{' '}
                      {testRecipients.filter((r) => r.enabled).length} selected
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1 border rounded-md p-2">
                      {testRecipients
                        .filter((r) => r.enabled)
                        .map((recipient) => (
                          <label
                            key={recipient.id}
                            className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTestRecipientIds.includes(
                                recipient.id,
                              )}
                              onCheckedChange={() =>
                                toggleTestRecipient(recipient.id)
                              }
                              disabled={sending || sendingTest}
                            />
                            <span className="flex-1">{recipient.label}</span>
                            {recipient.msisdn && (
                              <Badge variant="outline">SMS</Badge>
                            )}
                            {recipient.email && (
                              <Badge variant="outline">Email</Badge>
                            )}
                            {recipient.appUserUuid && (
                              <Badge variant="outline">Inbox/Push</Badge>
                            )}
                            {recipient.mvnoId && (
                              <Badge variant="secondary">
                                {recipient.mvnoId}
                              </Badge>
                            )}
                          </label>
                        ))}
                    </div>
                  </div>
                )}
              {skippedTestRecipients.length > 0 && (
                <Alert variant="warning" className="mt-2">
                  <AlertDescription>
                    <p className="font-medium mb-1">
                      {skippedTestRecipients.length} recipient
                      {skippedTestRecipients.length !== 1 ? 's' : ''} skipped:
                    </p>
                    {/* DEV-1957: a large managed-recipient pool could skip
                        arbitrarily many, growing this alert without bound. */}
                    <ul className="list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                      {skippedTestRecipients.map((skipped) => (
                        <li key={skipped.id}>
                          {skipped.label}: {skipped.reason}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleSendTest}
                disabled={
                  sending ||
                  sendingTest ||
                  !campaignName.trim() ||
                  !!validateCampaign()
                }
              >
                {sendingTest ? (
                  <IconLoader2 size={16} className="animate-spin" />
                ) : (
                  <IconSend size={16} />
                )}
                {sendingTest
                  ? 'Sending Test...'
                  : multiChannel
                    ? `Send Test (${selectedChannels.join(' + ')})`
                    : testRecipients.filter((r) => r.enabled).length === 0
                      ? 'Send Test SMS (legacy recipients)'
                      : `Send Test SMS (${selectedTestRecipientIds.length} recipient${selectedTestRecipientIds.length !== 1 ? 's' : ''})`}
              </Button>
              {testError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>{testError}</AlertDescription>
                </Alert>
              )}
              {testSuccess && (
                <Alert variant="success" className="mt-2">
                  <AlertDescription>{testSuccess}</AlertDescription>
                </Alert>
              )}
              {/* DEV-1957: the SMS-only "test sent" info alert that used to sit
                  here duplicated `testSuccess` verbatim ("Test SMS sent
                  successfully to N test recipient(s)") and only added height.
                  `testError` and `testSuccess` are mutually exclusive — every
                  test handler clears both before it starts and then sets
                  exactly one — so one banner is the whole outcome. */}
            </div>

            {sendError && (
              <Alert variant="destructive" className="mt-2">
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            )}
            {sendWarning && (
              <Alert variant="warning" className="mt-2">
                <AlertDescription>{sendWarning}</AlertDescription>
              </Alert>
            )}
            {sendSuccess && (
              <Alert variant="success" className="mt-2">
                <AlertDescription>{sendSuccess}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSendDialogOpen(false);
                setCampaignName('');
                setMessages(['']);
                setAbTestEnabled(false);
                setSendError('');
                setSendWarning('');
                setSendSuccess('');
                setTestSent(false);
                setTestError('');
                setTestSuccess('');
                setSkippedTestRecipients([]);
              }}
              disabled={sending || sendingTest}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSendClick}
              disabled={
                sending ||
                !campaignName.trim() ||
                !!validateCampaign() ||
                (previewCount !== null && previewCount >= 4 && !testSent)
              }
            >
              <IconSend size={16} />
              {(() => {
                const shouldRequireTest =
                  previewCount !== null && previewCount >= 4;
                if (shouldRequireTest && !testSent) return 'Send Test First';
                return 'Schedule Campaign';
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onOpenChange={(open) => {
          if (!open && !sending) setConfirmDialogOpen(false);
        }}
      >
        {/* DEV-1957: same bounded-height + scrolling-body shape as the send
            dialog — the confirm summary grows with every enabled channel and
            variant, and its Confirm button must never leave the viewport. */}
        <DialogContent className="sm:max-w-2xl grid-rows-[auto_1fr_auto] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Confirm {selectedChannels.join(' + ') || 'SMS'} Campaign
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to send this campaign?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto py-2 -mx-4 px-4">
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div>
                <p className="text-sm font-medium">Campaign Name:</p>
                <p className="text-sm">{campaignName}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Recipients:</p>
                {multiChannel && sendMode === 'schedule' ? (
                  <p className="text-sm">
                    {previewCountLabel(previewCount ?? 0, 'schedule')} per
                    channel per brand across {selectedMvnoIds.length} brand
                    {selectedMvnoIds.length !== 1 ? 's' : ''}
                  </p>
                ) : (
                  <p className="text-sm">
                    up to {previewCount !== null ? previewCount : 0} user
                    {(previewCount !== null ? previewCount : 0) !== 1
                      ? 's'
                      : ''}{' '}
                    per channel per brand across {selectedMvnoIds.length} brand
                    {selectedMvnoIds.length !== 1 ? 's' : ''}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {brandLabels.join(', ')}
                </p>
              </div>
              {messages.length > 1 && (
                <div>
                  <p className="text-sm font-medium">AB Testing:</p>
                  <p className="text-sm">
                    {messages.length} message variant
                    {messages.length !== 1 ? 's' : ''} - Recipients will be
                    split evenly
                  </p>
                </div>
              )}
              {smsEnabled && (
                <div>
                  <p className="text-sm font-medium">
                    SMS Variant{messages.length > 1 ? 's' : ''}:
                  </p>
                  {messages
                    .filter((m) => m.trim())
                    .map((msg, index) => (
                      <div
                        key={index}
                        className="mt-2 p-3 bg-background rounded-lg border border-border"
                      >
                        <p className="text-xs font-bold mb-1">
                          Variant {index + 1} ({msg.length}/{MAX_MESSAGE_LENGTH}{' '}
                          chars)
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg}</p>
                      </div>
                    ))}
                </div>
              )}
              {pushEnabled && (
                <div>
                  <p className="text-sm font-medium">Push Notification:</p>
                  <div className="mt-2 p-3 bg-background rounded-lg border border-border">
                    <p className="text-xs font-bold mb-1">
                      {pushVariant.title}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">
                      {pushVariant.body}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Only devices with an active push subscription receive this;
                    the rest are recorded as skipped, not failed.
                  </p>
                </div>
              )}
              {inboxEnabled && (
                <div>
                  <p className="text-sm font-medium">
                    In-App Inbox ({inboxVariant.category}):
                  </p>
                  <div className="mt-2 p-3 bg-background rounded-lg border border-border">
                    <p className="text-xs font-bold mb-1">
                      {inboxVariant.headline}
                    </p>
                    <p className="text-sm">{inboxVariant.preview}</p>
                    <p className="text-sm whitespace-pre-wrap mt-2 text-muted-foreground">
                      {inboxVariant.fullMessage}
                    </p>
                    {inboxVariant.imageUrl.trim() && (
                      <p className="text-xs text-muted-foreground mt-2 break-all">
                        Image: {inboxVariant.imageUrl.trim()}
                      </p>
                    )}
                    {inboxVariant.videoUrl.trim() && (
                      <p className="text-xs text-muted-foreground mt-2 break-all">
                        Video: {inboxVariant.videoUrl.trim()}
                      </p>
                    )}
                    {inboxVariant.ctaUrl.trim() && (
                      <p className="text-xs text-muted-foreground mt-2 break-all">
                        Link: {inboxVariant.ctaUrl.trim()}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {emailEnabled && (
                <div>
                  <p className="text-sm font-medium">
                    Email (Brevo template {emailVariant.brevoTemplateId}):
                  </p>
                  <div className="mt-2 p-3 bg-background rounded-lg border border-border">
                    <p className="text-xs font-bold mb-1">
                      {emailVariant.subject}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      The body comes from the Brevo template
                      {emailVariant.params.filter((param) => param.key.trim())
                        .length > 0
                        ? `, with ${emailVariant.params.filter((param) => param.key.trim()).length} param(s): ${emailVariant.params
                            .filter((param) => param.key.trim())
                            .map(
                              (param) => `${param.key.trim()}=${param.value}`,
                            )
                            .join(', ')}`
                        : ', with no params'}
                      .
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    One email per address: customers sharing a mailbox receive
                    one. An unclear Brevo outcome is recorded BLOCKED and is
                    never retried automatically.
                  </p>
                </div>
              )}
            </div>

            {((previewCount !== null && previewCount >= 4 && testSent) ||
              (previewCount !== null && previewCount < 4)) && (
              <Alert variant="info">
                <AlertDescription>
                  {previewCount !== null && previewCount < 4
                    ? `✓ Campaign will be sent directly to ${previewCount} user${previewCount !== 1 ? 's' : ''} (test skipped for small campaigns).`
                    : '✓ Test SMS has been sent successfully. You can now proceed with scheduling the campaign.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Preserve the existing scheduling affordance: it appears only
                for multi-channel or multi-brand compositions. */}
            {multiChannel && (
              <div className="space-y-2">
                <Label>When</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={sendMode === 'now' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMode('now')}
                    disabled={sending}
                  >
                    Send now
                  </Button>
                  <Button
                    type="button"
                    variant={sendMode === 'schedule' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMode('schedule')}
                    disabled={sending}
                  >
                    <IconClock size={14} className="mr-1" />
                    Schedule
                  </Button>
                </div>
                {sendMode === 'schedule' && (
                  <div>
                    <Label htmlFor="scheduled-at">
                      Release time — SAST (Africa/Johannesburg)
                    </Label>
                    <Input
                      id="scheduled-at"
                      type="datetime-local"
                      value={scheduledAtLocal}
                      onChange={(e) => setScheduledAtLocal(e.target.value)}
                      disabled={sending}
                      // Codex fix C21(a): the picker itself refuses anything
                      // under the lead time, matching (approximately — the
                      // server is the real authority, this is a UX nicety)
                      // what the backend would reject anyway.
                      min={toSastDatetimeLocalMin(
                        new Date(Date.now() + 15 * 60_000),
                      )}
                      // Codex fix C21(d): suppresses the seconds picker some
                      // browsers show for datetime-local — a seconds-bearing
                      // value would make `${scheduledAtLocal}:00+02:00`
                      // malformed (e.g. "...T10:00:30:00+02:00").
                      step={60}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Must be at least 15 minutes from now and inside the
                      WASPA/CPA marketing-hours window (Mon-Fri 08:00-20:00, Sat
                      09:00-13:00, Sundays and SA public holidays blocked) — the
                      server enforces both and returns an error naming the
                      reason.
                    </p>
                  </div>
                )}
              </div>
            )}

            <Alert variant="warning">
              <AlertDescription>
                {/* Gate scheduling copy on the same multi-channel condition as
                    the controls above so a hidden, stale sendMode can never
                    describe an immediate send as scheduled. */}
                {multiChannel && sendMode === 'schedule'
                  ? `This campaign will be RELEASED (not sent now) at the time above. The displayed count is an estimate only; the audience will be recalculated from current snapshots at release across ${selectedMvnoIds.length} brand${selectedMvnoIds.length !== 1 ? 's' : ''}.`
                  : `This action cannot be undone. The campaign will be sent immediately to every matched customer across ${selectedMvnoIds.length} brand${selectedMvnoIds.length !== 1 ? 's' : ''} (up to ${previewCount !== null ? previewCount : 0} per channel per brand).`}
              </AlertDescription>
            </Alert>

            {/* Codex fix C21(c): rendered HERE, not only in the send dialog
                behind this one — a rejection (schedule validation or any
                other 400) now leaves this dialog open with its own error
                visible, in the same place the operator just clicked
                "Confirm". */}
            {sendError && (
              <Alert variant="destructive">
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            )}
            {sendWarning && (
              <Alert variant="warning">
                <AlertDescription>{sendWarning}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                // Codex fix C5: don't let a schedule selection leak into the
                // next time this dialog opens for a different composition.
                setSendMode('now');
                setScheduledAtLocal('');
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmSend}
              disabled={
                sending ||
                (multiChannel && sendMode === 'schedule' && !scheduledAtLocal)
              }
            >
              {sending ? (
                <IconLoader2 size={16} className="animate-spin" />
              ) : (
                <IconSend size={16} />
              )}
              {sending
                ? sendMode === 'schedule'
                  ? 'Scheduling...'
                  : 'Sending...'
                : multiChannel && sendMode === 'schedule'
                  ? 'Confirm & Schedule'
                  : 'Confirm & Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DEV-1949: saved brand groups. Both refetch through loadBrandGroups()
          rather than patching a local copy — groups are shared across
          operators, so the list must come from the server after every write. */}
      <SaveGroupDialog
        open={saveGroupOpen}
        onOpenChange={setSaveGroupOpen}
        selectedIds={selectedMvnoIds}
        groups={brandGroups}
        onChanged={loadBrandGroups}
      />
      <BrandGroupsDialog
        open={manageGroupsOpen}
        onOpenChange={setManageGroupsOpen}
        groups={brandGroups}
        options={availableMvnos.map((mvno) => ({
          id: mvno.id,
          label: mvno.label,
        }))}
        onChanged={loadBrandGroups}
      />

      {/* Full-page loader overlay when sending campaign */}
      {sending && (
        <div className="fixed inset-0 bg-black/50 flex flex-col items-center justify-center z-[9999]">
          <div className="bg-background p-8 rounded-lg flex flex-col items-center gap-4">
            <IconLoader2 className="h-14 w-14 animate-spin text-primary" />
            <h3 className="text-lg font-semibold">Accepting Campaign...</h3>
            <p className="text-sm text-muted-foreground text-center max-w-[400px]">
              Validating the audience and creating durable background work.
              Large inbox audiences continue preparing after this page returns.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
